import { NavLink, useLocation } from 'react-router-dom';
import { Home, Search, Bell, Heart } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth';

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  getTo?: (isAuthenticated: boolean) => string;
}

const navItems: NavItem[] = [
  {
    to: '/',
    icon: Home,
    label: 'Home',
  },
  {
    to: '/listings',
    icon: Search,
    label: 'Cerca',
  },
  {
    to: '/searches',
    icon: Bell,
    label: 'Lead',
    getTo: (isAuthenticated: boolean) => (isAuthenticated ? '/searches?tab=leads' : '/login'),
  },
  {
    to: '/favorites',
    icon: Heart,
    label: 'Salvati',
    getTo: (isAuthenticated: boolean) => (isAuthenticated ? '/favorites' : '/login'),
  },
];

export function BottomNav(): JSX.Element {
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();

  const isActive = (item: NavItem): boolean => {
    const path = item.to;
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/95 backdrop-blur-lg border-t border-gray-200"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex h-16 items-center justify-around px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const to = item.getTo ? item.getTo(isAuthenticated) : item.to;
          const active = isActive(item);

          return (
            <NavLink
              key={item.label}
              to={to}
              className={cn(
                'relative flex flex-col items-center justify-center min-w-[64px] min-h-[56px] px-3 py-2',
                'transition-all duration-200 rounded-2xl',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                'active:scale-95',
                active && 'bg-blue-50'
              )}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
            >
              {/* Indicatore attivo - pallino sopra l'icona */}
              {active && (
                <span className="absolute top-1.5 w-1 h-1 rounded-full bg-blue-500" />
              )}

              <Icon
                className={cn(
                  'w-6 h-6 transition-all duration-200',
                  active ? 'text-blue-600' : 'text-gray-400'
                )}
              />
              <span
                className={cn(
                  'text-[11px] font-medium mt-1 transition-all duration-200',
                  active ? 'text-blue-600' : 'text-gray-500'
                )}
              >
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
